import React, { useEffect, useState } from 'react';
import { createReview, updateReview } from '../../api/reviews';
import type { Review } from '../../features/reviews/types';

interface ReviewModalProps {
  show: boolean;
  onHide: () => void;
  onReviewSaved: () => void;
  item?: Review | null;
}

const ReviewModal: React.FC<ReviewModalProps> = ({ show, onHide, onReviewSaved, item }) => {
  const [formData, setFormData] = useState({
    quote: '',
    author: '',
    displayOrder: 0,
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (item) {
      setFormData({
        quote: item.quote,
        author: item.author,
        displayOrder: item.displayOrder,
        isActive: item.isActive,
      });
    } else {
      setFormData({
        quote: '',
        author: '',
        displayOrder: 0,
        isActive: true,
      });
    }
    setError(null);
  }, [item, show]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement | HTMLTextAreaElement;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      if (!formData.quote.trim()) {
        setError('Quote is required');
        return;
      }
      if (!formData.author.trim()) {
        setError('Author is required');
        return;
      }
      const payload = {
        quote: formData.quote.trim(),
        author: formData.author.trim(),
        displayOrder: Number(formData.displayOrder) || 0,
        isActive: formData.isActive,
      };
      if (item) {
        await updateReview(item.id, payload);
      } else {
        await createReview(payload);
      }
      onReviewSaved();
      onHide();
    } catch (err) {
      console.error('Review save error:', err);
      setError('An error occurred while saving the review');
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="bi bi-chat-quote me-2" />
              {item ? 'Edit Review' : 'Add Review'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onHide} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2" />
                  {error}
                </div>
              )}

              <div className="row g-3">
                <div className="col-12 col-md-8">
                  <label htmlFor="author" className="form-label">
                    <i className="bi bi-person me-2" />
                    Author *
                  </label>
                  <input
                    id="author"
                    name="author"
                    type="text"
                    className="form-control"
                    value={formData.author}
                    onChange={handleInputChange}
                    placeholder="Name shown under the quote"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label">Status</label>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    <label className="form-check-label" htmlFor="isActive">
                      Active (show on home page)
                    </label>
                  </div>
                </div>

                <div className="col-12">
                  <label htmlFor="quote" className="form-label">
                    <i className="bi bi-quote me-2" />
                    Quote *
                  </label>
                  <textarea
                    id="quote"
                    name="quote"
                    className="form-control"
                    rows={4}
                    value={formData.quote}
                    onChange={handleInputChange}
                    placeholder="Write the testimonial quote..."
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label htmlFor="displayOrder" className="form-label">
                    <i className="bi bi-sort-numeric-down me-2" />
                    Display Order
                  </label>
                  <input
                    id="displayOrder"
                    name="displayOrder"
                    type="number"
                    className="form-control"
                    min={0}
                    value={formData.displayOrder}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  />
                  <small className="text-muted">Lower numbers appear first on the home page.</small>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onHide} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2" />
                    {item ? 'Update Review' : 'Create Review'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;

